import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import "./Ui89InputSelect.css"
import "../style/input-box.css"
import "../style/text.css"
import {
  Ui89VirtualList,
  Ui89VirtualListPropsRenderRowProps,
} from "./Ui89VirtualList"
import { Ui89Scene } from "./Ui89Scene"
import { Ui89InputText, Ui89InputTextRef } from "./Ui89InputText"
import { Ui89Popover } from "./Ui89Popover"
import RenderCallback from "./private/RenderCallback"

export interface Ui89InputSelectProps<T> {
  /**
   * Available options.
   */
  options?: T[]

  /**
   * Option height. Options list uses a technique called DOM virtualization.
   */
  optionHeight?: number

  /**
   * The selected option.
   */
  value?: T

  /**
   * Display search input.
   *
   * Does not search automatically. You must filter the options yourself.
   */
  search?: boolean

  /**
   * Called whenever an option is selected.
   */
  onChange?: (option: T) => void

  /**
   * Called when the menu is opened or closed.
   */
  onOpenChange?: (open: boolean) => void

  /**
   * A search term was provided.
   */
  onSearch?: (search: string) => void

  /**
   * Retrieves the value that uniquely identifies the option. This is what is
   * used to determine which option is currently selected.
   */
  getOptionKey?: (option: T) => any

  /**
   * Customize how an option should be rendered.
   */
  renderOption?: (option: T) => any

  /**
   * Minimum width of the menu. The menu is still going to shrink if it
   * can't fit in the viewport.
   */
  menuOverflowMaxWidth?: number
}

/**
 * This is a very performant and customizable dropdown selector that
 * allows you to choose from a list of options.
 */
export function Ui89InputSelect<T>(props: Ui89InputSelectProps<T>) {
  const [search, setSearch] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const searchInput = useRef<Ui89InputTextRef>(null)

  const getOptionKey = useMemo(() => {
    return props.getOptionKey ?? ((option: any) => option)
  }, [props.getOptionKey])

  const options = useMemo(() => {
    if (props.options === undefined) {
      return []
    }

    return props.options
  }, [props.options])

  function isOptionSelected(option: T) {
    if (props.value === undefined) {
      // Definitely not selected.
      return false
    }

    return getOptionKey(option) === getOptionKey(props.value)
  }

  function optionTitle(option: T) {
    return String(option)
  }

  const selectOption = useCallback(
    (option: T) => {
      if (props.onChange !== undefined) {
        props.onChange(option)
      }

      setIsOpen(false)
    },
    [props.onChange],
  )

  const renderOption = useCallback(
    ({ row }: Ui89VirtualListPropsRenderRowProps<T>) => {
      const isSelected = isOptionSelected(row)

      return (
        <div
          className={[
            "ui89-input-select__menu__item",
            isSelected ? "ui89-input-select__menu__item--selected" : null,
          ].join(" ")}
          title={optionTitle(row)}
          key={getOptionKey(row)}
          onClick={() => selectOption(row)}
        >
          {props.renderOption !== undefined ? props.renderOption(row) : row}
        </div>
      )
    },
    [options, selectOption],
  )

  useEffect(() => {
    props.onOpenChange?.(isOpen)

    if (isOpen) {
      setSearch("")
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      if (props.onSearch !== undefined) {
        props.onSearch(search)
      }
    }
  }, [search])

  function onRenderPopover() {
    // Making sure that the search input is focused when opening the menu.
    searchInput.current?.focus()
  }

  return (
    <div className="ui89-input-select">
      <Ui89Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        popoverOverflowMaxWidth={props.menuOverflowMaxWidth}
        renderContainer={(props2) => (
          <div
            ref={props2.setRef}
            className={[
              "ui89-input-box",
              "ui89-input-box--unselectable",
              "ui89-input-box--clickable",
              "ui89-text-single-line",
            ].join(" ")}
            tabIndex={0}
            title={
              props.value !== undefined ? optionTitle(props.value) : undefined
            }
            {...props2.props}
          >
            {props.value !== undefined ? (
              <>
                {props.renderOption !== undefined
                  ? props.renderOption(props.value)
                  : props.value}
              </>
            ) : (
              <>Select...</>
            )}
          </div>
        )}
        renderPopover={() => (
          <div className="ui89-input-select__menu">
            <RenderCallback onRender={onRenderPopover} />

            {props.search && (
              <Ui89InputText
                ref={searchInput}
                placeholder="Search..."
                value={search}
                onChange={setSearch}
              />
            )}

            {options.length > 0 ? (
              <Ui89VirtualList
                maxHeight="300px"
                rowHeight={props.optionHeight ?? 32}
                rows={options}
                renderRow={renderOption}
              />
            ) : (
              <div
                className={[
                  "ui89-input-select__menu__item",
                  "ui89-input-select__menu__item--disabled",
                ].join(" ")}
              >
                &lt;empty&gt;
              </div>
            )}
          </div>
        )}
      />
    </div>
  )
}
